import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { fetchServiceMenuItems, NavigationMenuItem } from '../../services/navigationService';
import { getFlagUrl } from '../../utils/flagUtils';

interface Service {
  id: string;
  slug: string;
  title: string;
  description: string;
  icon: string;
  flag: string;
  flagEmoji: string;
  countryCode: string;
  features: string[];
  color: string;
}

const ServicesSummary: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Color gradients for different services
  const colorGradients = [
    'from-amber-500 to-orange-600',
    'from-green-500 to-emerald-600',
    'from-red-500 to-blue-600',
    'from-purple-500 to-pink-600',
    'from-blue-500 to-cyan-600',
    'from-pink-500 to-rose-600'
  ];

  // Icons for different services (fallback if not in translations)
  const iconFallbacks: Record<string, string> = {
    'egypt': '🏛️',
    'saudi-arabia': '🕌',
    'usa': '🗽',
    'uk': '👑',
    'uae': '🏙️',
    'canada': '🍁'
  };

  // Fetch dynamic services from service pages
  useEffect(() => {
    const loadServices = async () => {
      try {
        const menuItems = await fetchServiceMenuItems(i18n.language);

        // Map menu items to services
        const dynamicServices: Service[] = menuItems.map((item: NavigationMenuItem, index: number) => {
          const serviceKey = item.slug.replace('-', '');

          // Use card customization from API if available, otherwise fall back to defaults
          const gradient = (item.card_gradient_from && item.card_gradient_to)
            ? `${item.card_gradient_from} ${item.card_gradient_to}`
            : colorGradients[index % colorGradients.length];

          const features = (item.card_features && item.card_features.length > 0)
            ? item.card_features
            : [
                t(`services.${serviceKey}.features.1`, 'Company Registration'),
                t(`services.${serviceKey}.features.2`, 'Business License'),
                t(`services.${serviceKey}.features.3`, 'Tax Registration'),
                t(`services.${serviceKey}.features.4`, 'Bank Account Support')
              ];

          const description = item.card_description || item.subtitle || t(`services.${serviceKey}.description`, `Establish your business in ${item.name}`);

          const icon = item.card_icon || iconFallbacks[item.slug] || '🏢';

          return {
            id: item.slug,
            slug: item.slug,
            title: item.title || t(`services.${serviceKey}.title`, `${item.name} Company Formation`),
            description: description,
            icon: icon,
            flag: getFlagUrl(item.country_code),
            flagEmoji: item.fallback,
            countryCode: item.country_code,
            features: features,
            color: gradient
          };
        });

        setServices(dynamicServices);
      } catch (error) {        setServices([]);
      }
    };

    loadServices();
  }, [i18n.language, t]);

  const handleLearnMore = (serviceSlug: string) => {
    navigate(`/services/${serviceSlug}`);
    // Scroll to top when navigating
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const scrollToContact = () => {
    navigate('/contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-primary-50 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 right-20 w-80 h-80 bg-gradient-to-r from-primary-200 to-primary-300 rounded-full opacity-20"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 120, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear" as const
          }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-64 h-64 bg-gradient-to-r from-primary-100 to-primary-200 rounded-full opacity-15"
          animate={{
            scale: [1, 0.8, 1],
            rotate: [0, -90, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "linear" as const
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('services.title', 'Our Global Services')}
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {t('services.subtitle', 'Expert company formation services tailored to each country\'s unique requirements and business environment.')}
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          ref={ref}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 group flex flex-col h-full"
              variants={cardVariants}
              whileHover={{
                y: -8,
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
            >
              {/* Service Header */}
              <div
                className={service.color.includes('#') ? 'p-6 text-white relative overflow-hidden' : `bg-gradient-to-r ${service.color} p-6 text-white relative overflow-hidden`}
                style={
                  service.color.includes('#')
                    ? {
                        background: `linear-gradient(to right, ${service.color.split(' ')[0]}, ${service.color.split(' ')[1] || service.color.split(' ')[0]})`
                      }
                    : {}
                }
              >
                <motion.div
                  className="absolute inset-0 bg-black opacity-10"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.8 }}
                />
                <div className="flex items-center space-x-4 rtl:space-x-reverse relative z-10">
                  <motion.div
                    className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden border-2 border-white/30"
                    whileHover={{ scale: 1.1, rotate: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src={service.flag}
                      alt={`${service.title} flag`}
                      className="w-12 h-8 object-cover rounded"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentNode as HTMLElement;
                        if (parent && !parent.querySelector('.flag-fallback')) {
                          const emojiSpan = document.createElement('span');
                          emojiSpan.className = 'flag-fallback text-2xl';
                          emojiSpan.textContent = service.flagEmoji || service.icon;
                          parent.appendChild(emojiSpan);
                        }
                      }}
                    />
                  </motion.div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-1">{service.title}</h3>
                  </div>
                </div>
              </div>

              {/* Service Content */}
              <div className="p-6 flex flex-col h-full">
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Features List */}
                <div className="space-y-3 mb-8 flex-1">
                  {service.features.map((feature, idx) => (
                    <motion.div
                      key={idx}
                      className="flex items-center space-x-3 rtl:space-x-reverse"
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.5 + idx * 0.1 }}
                    >
                      <motion.div
                        className={`w-2 h-2 rounded-full bg-gradient-to-r ${service.color}`}
                        whileHover={{ scale: 1.5 }}
                      />
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Action Button */}
                <motion.button
                  onClick={() => handleLearnMore(service.id)}
                  className={
                    service.color.includes('#')
                      ? 'w-full py-3 px-6 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 relative overflow-hidden group mt-auto'
                      : `w-full py-3 px-6 rounded-lg font-semibold text-white bg-gradient-to-r ${service.color} hover:shadow-lg transition-all duration-200 relative overflow-hidden group mt-auto`
                  }
                  style={
                    service.color.includes('#')
                      ? {
                          background: `linear-gradient(to right, ${service.color.split(' ')[0]}, ${service.color.split(' ')[1] || service.color.split(' ')[0]})`
                        }
                      : {}
                  }
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-white opacity-20"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 0.6 }}
                  />
                  <span className="relative z-10">
                    {t('services.learnMore', 'Learn More')} →
                  </span>
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto"
            whileHover={{ y: -5 }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('services.cta.title', 'Ready to Start Your Business Journey?')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('services.cta.description', 'Our experts are ready to guide you through every step of the company formation process.')}
            </p>
            <motion.button
              onClick={scrollToContact}
              className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg hover:bg-primary-700 transition-all duration-200"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('services.cta.button', 'Get Free Consultation')}
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSummary;
